/**
 * The right-hand panel: the selected block's properties, or the letter's own.
 *
 * Letter settings are always reachable rather than hidden behind "deselect
 * everything" - the width and the font are the two settings somebody changes
 * most often, and hunting for the empty spot on a canvas to get at them is the
 * kind of small friction that makes a builder feel hostile.
 */
import { Mail, SlidersHorizontal } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { blockMeta } from "../catalog";
import { useBuilder } from "../store";
import { FONT_STACKS, type PageStyle } from "../types";
import { BlockFields } from "./BlockFields";
import { ChoiceField, ColorField, Group, NumberField } from "./controls";

export function Inspector({ onPickImage }: { onPickImage?: (apply: (url: string) => void) => void }) {
  const t = useT();
  const { design, selected, dispatch } = useBuilder();
  const meta = selected ? blockMeta(selected.type) : null;

  return (
    <Tabs value={selected ? "block" : "page"} onValueChange={(v) => v === "page" && dispatch({ kind: "select", id: null })}>
      <TabsList className="w-full">
        <TabsTrigger value="block" disabled={!selected} className="flex-1 gap-1.5 text-xs">
          <SlidersHorizontal className="size-3.5" />
          {meta ? t(`marketing.builder.block.${meta.key}`) : t("marketing.builder.noSelection")}
        </TabsTrigger>
        <TabsTrigger value="page" className="flex-1 gap-1.5 text-xs">
          <Mail className="size-3.5" />
          {t("marketing.builder.letterTab")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="block" className="mt-4 space-y-4">
        {selected ? (
          <BlockFields block={selected} page={design.page} onPickImage={onPickImage} />
        ) : (
          <p className="text-xs text-muted-foreground">{t("marketing.builder.selectHint")}</p>
        )}
      </TabsContent>

      <TabsContent value="page" className="mt-4 space-y-4">
        <PageFields page={design.page} onChange={(patch) => dispatch({ kind: "page", patch })} />
      </TabsContent>
    </Tabs>
  );
}

function PageFields({ page, onChange }: { page: PageStyle; onChange: (patch: Partial<PageStyle>) => void }) {
  const t = useT();
  return (
    <>
      <Group title={t("marketing.builder.group.layout")}>
        <NumberField
          label={t("marketing.builder.prop.contentWidth")}
          hint={t("marketing.builder.prop.contentWidthHint")}
          value={page.contentWidth}
          min={320}
          max={800}
          suffix="px"
          onChange={(v) => onChange({ contentWidth: v })}
        />
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label={t("marketing.builder.prop.paddingX")}
            value={page.paddingX}
            max={80}
            suffix="px"
            onChange={(v) => onChange({ paddingX: v })}
          />
          <NumberField
            label={t("marketing.builder.prop.paddingY")}
            value={page.paddingY}
            max={80}
            suffix="px"
            onChange={(v) => onChange({ paddingY: v })}
          />
        </div>
        <NumberField
          label={t("marketing.builder.prop.radius")}
          value={page.radius}
          max={40}
          suffix="px"
          onChange={(v) => onChange({ radius: v })}
        />
      </Group>

      <Group title={t("marketing.builder.group.typography")}>
        <ChoiceField
          label={t("marketing.builder.prop.font")}
          hint={t("marketing.builder.prop.fontHint")}
          value={page.fontFamily}
          onChange={(v) => onChange({ fontFamily: v })}
          options={FONT_STACKS.map((f) => ({ value: f.id, label: f.label }))}
        />
        <NumberField
          label={t("marketing.builder.prop.baseSize")}
          value={page.fontSize}
          min={12}
          max={24}
          suffix="px"
          onChange={(v) => onChange({ fontSize: v })}
        />
      </Group>

      <Group title={t("marketing.builder.group.colors")}>
        <div className="grid grid-cols-2 gap-2">
          <ColorField
            label={t("marketing.builder.prop.cardColor")}
            value={page.cardColor}
            clearable={false}
            onChange={(v) => onChange({ cardColor: v })}
          />
          <ColorField
            label={t("marketing.builder.prop.pageColor")}
            value={page.backgroundColor}
            clearable={false}
            onChange={(v) => onChange({ backgroundColor: v })}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ColorField
            label={t("marketing.builder.prop.textColor")}
            value={page.textColor}
            clearable={false}
            onChange={(v) => onChange({ textColor: v })}
          />
          <ColorField
            label={t("marketing.builder.prop.linkColor")}
            value={page.linkColor}
            clearable={false}
            onChange={(v) => onChange({ linkColor: v })}
          />
        </div>
      </Group>
    </>
  );
}
